<script setup>
import { cookieRef } from '@/@layouts/stores/config'
import AssortedFeedbackQuestion from '@/views/feedback/feedback-entry/tabs/AssortedFeedbackQuestion.vue'
import CustomerFetchDialog from '@/views/customer/dialogs/CustomerFetchDialog.vue'
import PreferenceDrawer from '@/views/feedback/preference/PreferenceDrawer.vue'
import { useFeedbackEntry } from '@core/stores/feedback-entry'

definePage({
  meta: {
    name: "dashboard-department",
    subject: "feedback.menu",
    action: "feedback_entry_ward_round",
  },
})

const unitId = cookieRef('selectedUnit')

// const { 
//   data: formTypes,
// } = await useApi(createUrl('masters/form-types', { 
//   query: { 
//     select: ['id', 'name'],
//     unit_id: unitId,
//   },
// }))

// const feedbackTypeRadio =  updateObjectAsSelect(formTypes.value)

const sourceType = ref()

await useApi(createUrl('masters/feedback-sources', { 
  query: { 
    search: {
      code: 'WARD-ROUND',
    },
  },
}), {
  afterFetch: ({ data }) => {
    sourceType.value = data[0]?.id ?? null
  },
})

const feedbackFormData = ref({})

const customerInfoFormData = ref({})
const isCustomerDialogOpen = ref(sourceType.value ? true : false)
const customer = ref(null)

const updateCustomerData = data => {
  customer.value = data

  useFeedbackEntry().updateCurrentFeedback({
    feedback_type: 'assorted',
    customer_type: 'PATIENT',
    unit_id: unitId,
    source_type: sourceType?.value, // Dynamic Ward Round ID
    form_type: data?.episode?.form_type?.id,
    customer: (() => {
      const { name, phone, email, uhid, age, ...rest } = data

      return rest
    })(),
    feedback: [],
  })
  isCustomerDialogOpen.value = false
}

const changeCustomer = () => {
  customer.value = null
  useFeedbackEntry().resetCurrentFeedback()
  isCustomerDialogOpen.value = true
}

watch(feedbackFormData, () => {
  if (Array.isArray(feedbackFormData.value) && feedbackFormData.value.length > 0){
    useFeedbackEntry().updateCurrentFeedback({
      feedback: feedbackFormData.value?.filter(item => {
        if (item.response !== null && item.response !== '' && item.response !== undefined) {
          return item
        }
      }),
    })
  }
}, { immediate: true, deep: true })

const loading = ref(false)

const onSubmit = async data => {
  loading.value = true
  useFeedbackEntry().currentFeedback.customer.feedback_datetime = customDateFormat(new Date())
  await useFeedbackEntry().submitCurrentFeedback(
    false, 
    () => {
      customer.value = null
      useFeedbackEntry().resetCurrentFeedback()
      loading.value = false
      isCustomerDialogOpen.value = true
    },
  )
  loading.value = false
}

const customDateFormat = date => {
  const pad = n => n.toString().padStart(2, '0')

  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
         `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

// Preference Dialog
const isPreferenceDrawerOpen = ref(false)

const currentCustomerId = computed(() => useFeedbackEntry().getFilledFeedback?.customer?.customer_id ?? null)
const currentEpisodeId = computed(() => useFeedbackEntry().getFilledFeedback?.customer?.episode_id ?? null)

onBeforeMount(() => {
  useFeedbackEntry().resetCurrentFeedback()
})
</script>

<template>
  <div
    v-if="sourceType"
    class="d-flex flex-column gap-6"
  >
    <VAlert
      variant="tonal"
      color="warning"
      icon="tabler-alert-triangle"
    >
      This page is <strong>deprecated</strong> in version v2.5.5. Please use
      <RouterLink
        class="text-decoration-underline"
        :to="{ name: 'app-feedback-entry-ward-round-v2' }"
      >
        Ward Round V2
      </RouterLink>
      instead.
    </VAlert>

    <VCard
      v-if="customer"
      variant="flat"
      class="border"
    >
      <VCardText>
        <div class="d-flex align-center justify-space-between">
          <h6 class="text-base font-weight-medium">
            <div v-if="customer?.uhid">
              UHID : {{ customer.uhid }}
            </div>
            <div v-if="customer?.episode_no">
              Episode No : {{ customer.episode_no }}
            </div>
            <div v-if="customer?.name">
              Name : {{ customer.name }}
            </div>
            <div v-if="customer?.phone">
              Mobile No : {{ customer.phone }}
            </div>
            <div v-if="customer?.email">
              Email : {{ customer.email }}
            </div>
          </h6>
          <div class="d-flex gap-2">
            <VBtn
              variant="outlined"
              color="primary"
              prepend-icon="tabler-empathize"
              @click="isPreferenceDrawerOpen = true"
            >
              Add Preference
            </VBtn>
            <VBtn
              color="secondary"
              variant="tonal"
              prepend-icon="tabler-replace-user"
              @click="changeCustomer"
            >
              Change {{ $t('Customer') }}
            </VBtn>
          </div>
        </div>
      </VCardText>
    </VCard>
    <div 
      v-else
      class="rounded border-dashed border-md px-6 py-8 d-flex align-center justify-center"
    >
      <div>
        <VBtn
          variant="tonal"
          @click="changeCustomer"
        >
          Select {{ $t('Customer') }}
        </VBtn>
      </div>
    </div>

    <!--
      <VCard
      v-if="!useFeedbackEntry().currentFeedback?.form_type && !isCustomerDialogOpen"
      title="Form Type"
      subtitle=""
      >
      <VCardText>
      <CustomRadiosWithOutLabel
      v-model:selected-radio="feedbackFormData.formType"
      :radio-content="feedbackTypeRadio"
      :rules="[requiredValidator]"
      >
      <template #content="{ item }">
      <VImg
      v-if="item.avatar"
      :width="150"
      :height="150"
      :src="item.avatar"
      alt="bg-img"
      >
      <template #error>
      <VIcon
      icon="tabler-alert-triangle"
      size="2x"
      class="text-danger"
      />
      </template>
      </VImg>
      <h6 class="text-base font-weight-medium">
      {{ item.title }}
      </h6>
      </template>
      </CustomRadiosWithOutLabel>
      </VCardText>
      </VCard> 
    -->

    <div class="mt-4">
      <AssortedFeedbackQuestion 
        v-if="useFeedbackEntry().currentFeedback?.form_type"
        @update:feedback-type-form-data="feedbackFormData = $event"
        @update:reset-feedback-form="resetFeedbackForm"
      />
    </div>

    <VBtn
      v-if="useFeedbackEntry().currentFeedback?.form_type"
      class="mt-4"
      color="success"
      block
      variant="flat"
      :loading="loading"
      @click="onSubmit"
    >
      Submit
    </VBtn>
  </div>
  <div v-else>
    <VAlert
      variant="tonal"
      color="error"
      icon="tabler-alert-triangle"
    >
      Ward Round (WARD-ROUND) Source is not configured.
    </VAlert>
  </div>

  <CustomerFetchDialog
    v-model:isDialogOpen="isCustomerDialogOpen"
    @update:customer-dialog-data="updateCustomerData"
    @update:clear-customer-selection="feedbackFormData.customerType = ''"
  />

  <PreferenceDrawer
    v-if="currentCustomerId"
    v-model:is-drawer-open="isPreferenceDrawerOpen"
    :customer-id="currentCustomerId"
    :episode-id="currentEpisodeId"
    :customer="customer"
  />
</template>
